import { Injectable, inject } from '@angular/core';
import {
  Firestore} from '@angular/fire/firestore';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Observable, from, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { User } from '@shared/definitions/user';
import { deepStripUndefined } from '@shared/utils/firestore-clean';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly USERS_COLLECTION = 'users';
  private auth = inject(Auth);

  constructor(private firestore: Firestore) {}

  getUser(userUID: string): Observable<User | null> {
    const userDocRef = doc(this.firestore, `${this.USERS_COLLECTION}/${userUID}`);
    return from(getDoc(userDocRef)).pipe(
      map(docSnap => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          return new User({ ...userData, userUID: docSnap.id });
        }
        return null;
      }),
      catchError(error => {
        console.error('👤 UserService: Error fetching user:', error);
        return of(null);
      })
    );
  }

  createUser(user: User): Observable<void> {
    const userDocRef = doc(this.firestore, `${this.USERS_COLLECTION}/${user.userUID}`);
    return from(
      setDoc(
        userDocRef,
        deepStripUndefined({ ...user, createdAt: new Date(), updatedAt: new Date() })
      )
    ).pipe(
      catchError(error => {
        console.error('Error creating user:', error);
        return of(void 0);
      })
    );
  }

  updateUser(userUID: string, userData: Partial<User>): Observable<void> {
    const userDocRef = doc(this.firestore, `${this.USERS_COLLECTION}/${userUID}`);
    const dataToSave = deepStripUndefined({ ...userData, updatedAt: new Date() });

    return from(updateDoc(userDocRef, dataToSave)).pipe(
      catchError(error => {
        console.error('👤 UserService: Error updating user:', error);
        console.error('👤 UserService: Error details:', error.message, error.code);

        // If document doesn't exist or permission denied (which can happen when doc doesn't exist), create it first
        if (error.code === 'not-found' || 
            error.code === 'permission-denied' || 
            error.message.includes('No document to update') ||
            error.message.includes('Missing or insufficient permissions')) {
          return this.createUserIfNotExists(userUID, userData);
        }
        
        // THROW the error for other cases
        throw error;
      })
    );
  }

  /**
   * Create user document if it doesn't exist, then update with provided data
   * Uses setDoc with merge to safely create or update the document
   */
  private createUserIfNotExists(userUID: string, userData: Partial<User>): Observable<void> {
    // Get email from Firebase Auth if available
    const currentUser = this.auth.currentUser;
    const email = currentUser?.email || userData.email || '';

    // Create a basic user object with required fields, using actual user data
    const defaultUser: Partial<User> = {
      userUID: userUID,
      email: email,
      isActive: true,
      roles: ['user'],
      createdAt: new Date(),
      updatedAt: new Date(),
      ...userData // Merge with provided data (this overwrites defaults if provided)
    };

    const userDocRef = doc(this.firestore, `${this.USERS_COLLECTION}/${userUID}`);

    // Use setDoc with merge:true to safely handle both create and update scenarios
    return from(setDoc(userDocRef, deepStripUndefined(defaultUser), { merge: true })).pipe(
      catchError(error => {
        console.error('👤 UserService: Error creating/updating user document:', error);
        throw error;
      })
    );
  }
}